import { getGuid } from './Utils'
import { User, SolverCommand, ElectricalCode } from './Types'
import { solverURI, isDebug } from './App.config'
import * as signalR from '@microsoft/signalr'

export const solverCSS = 'padding: 4px 6px; background: #003462; color: #f47e56; font-size: 1rem; border-left: 2px solid green'

export const setConnection = (): signalR.HubConnection => {
    const hubConnection: signalR.HubConnection = new signalR.HubConnectionBuilder()
        .withUrl(solverURI)
        .configureLogging(signalR.LogLevel.Information)
        .build()
    isDebug && console.log('%cSignalR Hub Connection:', solverCSS, hubConnection)
    return hubConnection
}

export const startConnection = async (connection: signalR.HubConnection, user: User, learningGUID: string, solverModule: string) => {
    if (connection.state !== signalR.HubConnectionState.Disconnected) return

    console.log('%cStarting connection...', solverCSS)
    const tempUser: any = user
    tempUser['learningGUID'] = learningGUID
    const payload = JSON.stringify({
        Id: getGuid(),
        GroupName: '',
        CommandName: SolverCommand.JoinGroup,
        CommandArguments: {
            groupName: '',
            user: tempUser,
            moduleId: solverModule
        }
    })
    await connection.start()
        .then(async () => {
            await connection.invoke('ProcessMessage', payload)
        })

}

export const stopConnection = (connection: signalR.HubConnection, userName: string, groupName: string) => {
    if (connection.state !== signalR.HubConnectionState.Connected) return

    console.log('%cStopping solver connection...', solverCSS)
    const payload = JSON.stringify({
        Id: getGuid(),
        GroupName: groupName,
        CommandName: SolverCommand.LeaveGroup,
        CommandArguments: {
            groupName: groupName,
            userName: userName
        }
    })
    connection.invoke('ProcessMessage', payload)
        .then(async () => {
            await connection.stop()
        })
}

export const sendMessage = async (connection: signalR.HubConnection, command: SolverCommand, groupName: string) => {
    isDebug && console.log('%cSending Message ' + command, solverCSS)
    const payload = JSON.stringify({
        Id: getGuid(),
        GroupName: groupName,
        CommandName: command,
        CommandArguments: {
            groupName: groupName,
        }
    })

    await connection.invoke('ProcessMessage', payload)
}

export const loadScene = async (scene: string, connection: signalR.HubConnection, groupName: string) => {
    if (!scene) throw new Error('Attempting to load scene with empty name.')
    isDebug && console.log(`%cTelling solver to load SCENE ${scene}`, solverCSS)
    const payload = JSON.stringify({
        Id: getGuid(),
        GroupName: groupName,
        CommandName: SolverCommand.LoadScene,
        CommandArguments: {
            sceneId: scene
        }
    })
    await connection.invoke('ProcessMessage', payload)
}

export const loadFault = async (connection: signalR.HubConnection, groupName: string, solverModule: string, faultID: string, electricalCode: ElectricalCode) => {
    if (!faultID) throw new Error('Error: attempting to load empty fault.')
    isDebug && console.log(`%cTelling solver to load fault ${faultID} for ${solverModule} in group ${groupName}.`, solverCSS)
    const payload = JSON.stringify({
        Id: getGuid(),
        GroupName: groupName,
        CommandName: SolverCommand.LoadFault,
        CommandArguments: {
            moduleId: solverModule,
            faultId: faultID,
            groupName: groupName,
            electricalCode: electricalCode
        }
    })
    await connection.invoke('ProcessMessage', payload)
}

export const exitFault = async (connection: signalR.HubConnection, groupName: string) => {
    isDebug && console.log(`%cTelling solver to Exit Fault.`, solverCSS)
    await sendMessage(connection, SolverCommand.ExitFault, groupName)
}

export const beginFault = async (connection: signalR.HubConnection, groupName: string) => {
    isDebug && console.log(`%cTelling solver to Begin Fault.`, solverCSS)
    await sendMessage(connection, SolverCommand.BeginFault, groupName)
}

export const completeFault = async (faultID: string, connection: signalR.HubConnection, groupName: string) => {
    const re = new RegExp('F(00[1-9]|0[1-9][0-9]|[1-9][0-9][0-9]|[A-Z]{3})')
    if (re.test(faultID)) {
        isDebug && console.log(`%cTelling solver to Complete Fault.`, solverCSS)
        await sendMessage(connection, SolverCommand.CompleteFault, groupName)
    }
}
